import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight, Volume2, Mic, Keyboard, Headphones, ShieldCheck,
  Users, Zap, FileText, Check, AlertCircle,
} from 'lucide-react';
import { useAuth } from '@/store/auth';
import { useAudio } from '@/store/audio';
import { headset, type HeadsetState } from '@/lib/headset';
import { MicLevelMeter } from '@/components/softphone/MicLevelMeter';
import clsx from 'clsx';

type Tab = 'audio' | 'shortcuts' | 'admin';

/**
 * Ortak ayarlar sayfası — eski topbar utility butonları (Settings/Keyboard/Admin)
 * burada birleştirildi. Sidebar'dan tek tıkla erişilir, tab'lı navigasyon.
 */
export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [tab, setTab] = useState<Tab>('audio');

  return (
    <div className="p-5 max-w-[1100px] mx-auto">
      <header className="mb-3">
        <h1 className="text-[18px] font-semibold tracking-tight text-slate-900 leading-tight">Ayarlarım</h1>
        <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1.5">
          <span>Müşteri Temsilcisi</span>
          <ChevronRight size={11} className="opacity-60" />
          <span>Ayarlarım</span>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-3">
        <TabBtn active={tab === 'audio'} onClick={() => setTab('audio')} icon={<Volume2 size={14} />} label="Ses & Cihaz" />
        <TabBtn active={tab === 'shortcuts'} onClick={() => setTab('shortcuts')} icon={<Keyboard size={14} />} label="Klavye Kısayolları" />
        {isAdmin && (
          <TabBtn active={tab === 'admin'} onClick={() => setTab('admin')} icon={<ShieldCheck size={14} />} label="Yönetim" />
        )}
      </div>

      {tab === 'audio' && <AudioPanel />}
      {tab === 'shortcuts' && <ShortcutsPanel />}
      {tab === 'admin' && isAdmin && <AdminHubPanel />}
    </div>
  );
}

function TabBtn({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: React.ReactNode; label: string }) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'flex items-center gap-1.5 px-3 py-2 -mb-px text-[12.5px] font-medium border-b-2 transition',
        active
          ? 'text-brand-700 border-brand-500'
          : 'text-slate-500 border-transparent hover:text-slate-800 hover:border-slate-300',
      )}
    >
      {icon}
      {label}
    </button>
  );
}

/* ─── AUDIO PANEL ─────────────────────────────────────────────── */

interface DeviceInfo { deviceId: string; label: string; }

function AudioPanel() {
  const audio = useAudio();
  const [mics, setMics] = useState<DeviceInfo[]>([]);
  const [speakers, setSpeakers] = useState<DeviceInfo[]>([]);
  const [permError, setPermError] = useState(false);
  const [hs, setHs] = useState<HeadsetState>(() => headset.current());

  useEffect(() => headset.subscribe(setHs), []);
  useEffect(() => {
    enumerate();
    navigator.mediaDevices.addEventListener?.('devicechange', enumerate);
    return () => navigator.mediaDevices.removeEventListener?.('devicechange', enumerate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function enumerate() {
    try {
      try { (await navigator.mediaDevices.getUserMedia({ audio: true })).getTracks().forEach((t) => t.stop()); } catch { /* */ }
      const list = await navigator.mediaDevices.enumerateDevices();
      setMics(list.filter((d) => d.kind === 'audioinput').map((d) => ({ deviceId: d.deviceId, label: d.label || 'Mikrofon' })));
      setSpeakers(list.filter((d) => d.kind === 'audiooutput').map((d) => ({ deviceId: d.deviceId, label: d.label || 'Hoparlör' })));
    } catch (e) {
      console.warn('[settings] enumerate failed', e);
      setPermError(true);
    }
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-2.5 max-w-4xl">
      {permError && (
        <div className="lg:col-span-2 px-3 py-2 bg-amber-50 text-amber-800 rounded-xl text-[12px] ring-1 ring-amber-100 flex items-start gap-2">
          <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          Cihaz isimlerini görmek için mikrofon iznini onayla.
        </div>
      )}

      <Card title="Mikrofon" icon={<Mic size={14} />}>
        <NativeSelect
          value={audio.micDeviceId}
          onChange={(v) => audio.update({ micDeviceId: v })}
          devices={mics}
        />
        <SliderRow label="Mikrofon Gain" value={audio.micGain} onChange={(v) => audio.update({ micGain: v })} />
        <MicLevelMeter />
      </Card>

      <Card title="Hoparlör & Zil" icon={<Volume2 size={14} />}>
        <NativeSelect
          value={audio.speakerDeviceId}
          onChange={(v) => audio.update({ speakerDeviceId: v })}
          devices={speakers}
        />
        <SliderRow label="Zil Sesi" value={audio.ringerVolume} onChange={(v) => audio.update({ ringerVolume: v })} />
      </Card>

      <Card title="Kulaklık (Jabra)" icon={<Headphones size={14} />}>
        <div className="flex items-center justify-between gap-2">
          <div className="text-[12px] text-slate-600 leading-snug">
            {!hs.supported
              ? 'WebHID bu sürümde desteklenmiyor'
              : hs.connected
                ? <>Bağlı: <strong className="text-slate-900">{hs.label}</strong></>
                : 'Bağlı değil — fiziksel cevap/kapat tuşları için bağla'}
          </div>
          {hs.supported && (
            hs.connected ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 px-2 py-0.5 rounded-full font-medium">
                <Check size={11} /> Bağlı
              </span>
            ) : (
              <button
                onClick={() => void headset.pair()}
                className="text-[12px] px-3 py-1.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-[#08140d] font-medium transition active:scale-95"
              >
                Bağla
              </button>
            )
          )}
        </div>
        {hs.error && (
          <div className="mt-2 px-2.5 py-1.5 bg-amber-50 ring-1 ring-amber-100 rounded-lg text-amber-800 text-[11px]">
            {hs.error}
          </div>
        )}
      </Card>

      <Card title="Ses Tercihleri" icon={<Volume2 size={14} />}>
        <ToggleRow
          label="Yankı İptali"
          desc="Hoparlör kullanıyorsan AÇ — yoksa karşı taraf kendi sesini echo duyar"
          checked={audio.echoCancellation}
          onChange={(v) => audio.update({ echoCancellation: v })}
        />
        <ToggleRow
          label="Gürültü Bastırma"
          desc="Arka plan sesini azaltır ama konuşmayı da boğabilir"
          checked={audio.noiseSuppression}
          onChange={(v) => audio.update({ noiseSuppression: v })}
        />
        <ToggleRow
          label="Otomatik Ses Seviyesi"
          desc="Karşı taraf mikrofonu uzakta hissetmesin diye AÇ — kapatırsan ham mic seviyesi gider"
          checked={audio.autoGainControl}
          onChange={(v) => audio.update({ autoGainControl: v })}
        />
        <p className="text-[10.5px] text-slate-400 mt-2 leading-snug">
          Ayar değişiklikleri bir sonraki çağrıda etkili olur.
        </p>
      </Card>
    </div>
  );
}

/* ─── SHORTCUTS PANEL ─────────────────────────────────────────── */

function ShortcutsPanel() {
  const items: { keys: string[]; label: string }[] = [
    { keys: ['Ctrl', 'Shift', 'A'], label: 'Çağrıyı cevapla' },
    { keys: ['Ctrl', 'Shift', 'D'], label: 'Çağrıyı reddet' },
    { keys: ['Ctrl', 'Shift', 'H'], label: 'Çağrıyı kapat' },
    { keys: ['Ctrl', 'Shift', 'M'], label: 'Mikrofonu sustur / aç' },
    { keys: ['Ctrl', 'Shift', 'P'], label: 'Beklet / devam' },
    { keys: ['Ctrl', 'Shift', 'Q'], label: 'Kuyruğa katıl / çık' },
  ];
  return (
    <div className="max-w-2xl bg-surface border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
      <ul className="divide-y divide-slate-100">
        {items.map((it) => (
          <li key={it.label} className="flex items-center justify-between px-5 py-3">
            <span className="text-[13px] text-slate-700">{it.label}</span>
            <span className="inline-flex gap-1">
              {it.keys.map((k, i) => (
                <kbd key={i} className="px-2 py-0.5 bg-slate-100 ring-1 ring-slate-200 rounded text-[11px] font-mono text-slate-700">
                  {k}
                </kbd>
              ))}
            </span>
          </li>
        ))}
      </ul>
      <p className="px-5 py-3 text-[11px] text-slate-500 bg-slate-50/60 border-t border-slate-100">
        Kısayollar yalnızca bir input alanına yazmıyorken çalışır.
      </p>
    </div>
  );
}

/* ─── ADMIN HUB PANEL ─────────────────────────────────────────── */

function AdminHubPanel() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl">
      <AdminCard
        to="/admin/users"
        icon={<Users size={18} />}
        title="Kullanıcılar"
        desc="Agent ve admin yetkileri, 3CX extension eşleştirme"
      />
      <AdminCard
        to="/admin/speed-dials"
        icon={<Zap size={18} />}
        title="Kısa Yollar"
        desc="Tüm temsilcilerin yan panelinde görünen ortak numaralar"
      />
      <AdminCard
        to="/admin/crm-templates"
        icon={<FileText size={18} />}
        title="CRM Şablonları"
        desc="Çağrı geldiğinde tarayıcıda açılan CRM popup URL'leri"
      />
    </div>
  );
}

function AdminCard({ to, icon, title, desc }: { to: string; icon: React.ReactNode; title: string; desc: string }) {
  return (
    <Link
      to={to}
      className="group bg-surface border border-slate-100 rounded-2xl shadow-sm p-3.5 hover:-translate-y-px hover:shadow-md hover:border-brand-200 transition"
    >
      <div className="w-9 h-9 rounded-xl bg-brand-100 text-brand-700 flex items-center justify-center mb-2.5 group-hover:bg-brand-500 group-hover:text-[#08140d] transition">
        {icon}
      </div>
      <div className="text-[14px] font-semibold text-slate-900 tracking-tight mb-1">{title}</div>
      <div className="text-[12px] text-slate-500 leading-snug">{desc}</div>
    </Link>
  );
}

/* ─── Reusable card + form atoms ──────────────────────────────── */

function Card({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="bg-surface border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
      <header className="flex items-center gap-2 px-3.5 py-2 border-b border-slate-100 text-[12.5px] font-semibold text-slate-700">
        <span className="w-6 h-6 rounded-md bg-brand-100 text-brand-700 flex items-center justify-center">{icon}</span>
        {title}
      </header>
      <div className="p-3.5 space-y-2.5">{children}</div>
    </section>
  );
}

function NativeSelect({ value, onChange, devices }: { value: string; onChange: (v: string) => void; devices: DeviceInfo[] }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none bg-surface text-slate-800 text-[13px] pl-3 pr-7 py-2 rounded-lg ring-1 ring-slate-200 hover:ring-slate-300 focus:ring-brand-400 focus:outline-none transition truncate"
      >
        <option value="default">Varsayılan</option>
        {devices.map((d) => (
          <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-[10px]">▼</span>
    </div>
  );
}

function SliderRow({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[12px] font-medium text-slate-600">{label}</span>
        <span className="text-[11px] font-mono text-slate-500">{value}%</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-brand-500"
      />
    </div>
  );
}

function ToggleRow({ label, desc, checked, onChange }: {
  label: string; desc?: string; checked: boolean; onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 accent-brand-500 flex-shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-medium text-slate-800">{label}</div>
        {desc && <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{desc}</div>}
      </div>
    </label>
  );
}
